import { isSystemAdmin } from "./systemRoles";

export const PROJECT_ROLE_VALUES = [
  "project_manager",
  "qa_qc_manager",
  "inspector",
  "safety_officer",
  "site_supervisor",
  "contractor",
  "client",
  "foreman",
  "supervisor",
  "general_foreman",
  "project_engineer",
  "operations_manager",
  "third_party",
];

// value + label
export const PROJECT_ROLES = [
  { value: "project_manager", label: "Project Manager" },
  { value: "qa_qc_manager", label: "QA/QC Manager" },
  { value: "inspector", label: "Inspector" },
  { value: "safety_officer", label: "Safety Officer" },
  { value: "site_supervisor", label: "Site Supervisor" },
  { value: "contractor", label: "Contractor" },
  { value: "client", label: "Client" },
  { value: "foreman", label: "Foreman" },
  { value: "supervisor", label: "Supervisor" },
  { value: "general_foreman", label: "General Foreman" },
  { value: "project_engineer", label: "Project Engineer (P.E.)" },
  { value: "operations_manager", label: "Field Operations Manager" },
  { value: "third_party", label: "3rd Party Testing Agency" },
];

export const PROJECT_ROLE_LABELS = PROJECT_ROLES.reduce((acc, r) => {
  acc[r.value] = r.label;
  return acc;
}, {});

export const normProjectRole = (role) => (role || "").toLowerCase();

// helpers Inspection
export const isProjectManager = (role) =>
  normProjectRole(role) === "project_manager";
export const isQaQcManager = (role) =>
  normProjectRole(role) === "qa_qc_manager";
export const isInspector = (role) => normProjectRole(role) === "inspector";
export const isSafetyOfficer = (role) =>
  normProjectRole(role) === "safety_officer";
export const isSiteSupervisor = (role) =>
  normProjectRole(role) === "site_supervisor";
export const isContractor = (role) => normProjectRole(role) === "contractor";
export const isClient = (role) => normProjectRole(role) === "client";
export const isForeman = (role) => normProjectRole(role) === "foreman";
export const isSupervisor = (role) =>
  ["supervisor", "general_foreman"].includes(normProjectRole(role));
export const isGeneralForeman = (role) =>
  normProjectRole(role) === "general_foreman";
export const isProjectEngineer = (role) =>
  normProjectRole(role) === "project_engineer";
export const isOperationsManager = (role) =>
  normProjectRole(role) === "operations_manager";

// Role groups used in workflows NCR
export const ROLE_GROUPS = {
  PM_OR_QA: new Set(["project_manager", "qa_qc_manager"]),
  QA_OR_PM_OR_INSPECTOR: new Set([
    "project_manager",
    "qa_qc_manager",
    "inspector",
  ]),
  FIELD_CREATORS: new Set(["inspector", "qa_qc_manager", "safety_officer"]),
};

// Project workspace tab access (moved from ProjectNavigation.js)
export const ROLE_TABS = {
  project_manager: [
    "dashboard",
    "equipment",
    "issues-control",
    "team",
    "inspections",
    "ncr",
    "rfi",
    "safety",
    "daily",
    "documents",
    "kb-intake",
    "photos",
    "materials",
    "submittals",
    "inspection-templates",
    "walk-scheduler",
  ],
  qa_qc_manager: [
    "dashboard",
    "equipment",
    "issues-control",
    "inspections",
    "ncr",
    "rfi",
    "daily",
    "materials",
    "documents",
    "kb-intake",
    "photos",
    "safety",
    "submittals",
    "inspection-templates",
    "team",
    "walk-scheduler",
    "operations-performance",
  ],
  inspector: [
    "dashboard",
    "equipment",
    "issues-control",
    "inspections",
    "rfi",
    "ncr",
    "submittals",
    "inspection-templates",
    "daily",
    "documents",
    "kb-intake",
    "photos",
    "materials",
    "team",
    "walk-scheduler",
  ],
  safety_officer: [
    "dashboard",
    "equipment",
    "safety",
    "daily",
    "documents",
    "photos",
    "inspections",
    "ncr",
  ],
  site_supervisor: [
    "dashboard",
    "equipment",
    "daily",
    "documents",
    "photos",
    "rfi",
    "inspections",
    "safety",
  ],
  contractor: [
    "dashboard",
    "equipment",
    "my-issues",
    "assigned-inspections",
    "materials",
    "ncr",
    "rfi",
    "documents",
    "photos",
    "submittals",
    "walk-scheduler",
  ],
  client: [
    "dashboard",
    "equipment",
    "my-issues",
    "assigned-inspections",
    "rfi",
    "documents",
    "photos",
    "submittals",
    "safety",
    "walk-scheduler",
  ],
  foreman: [
    "dashboard",
    "equipment",
    "my-issues",
    "ncr",
    "walk-scheduler",
    "documents",
    "team",
  ],
  general_foreman: [
    "dashboard",
    "equipment",
    "my-issues",
    "issues-control",
    "ncr",
    "walk-scheduler",
    "documents",
    "team",
  ],
  project_engineer: [
    "dashboard",
    "equipment",
    "engineering-issues",
    "issues-control",
    "ncr",
    "daily",
    "walk-scheduler",
    "documents",
    "rfi",
    "submittals",
    "team",
  ],
  supervisor: [
    "dashboard",
    "equipment",
    "issues-control",
    "inspections",
    "ncr",
    "daily",
    "walk-scheduler",
    "documents",
    "team",
  ],
  operations_manager: [
    "dashboard",
    "equipment",
    "issues-control",
    "inspections",
    "ncr",
    "daily",
    "walk-scheduler",
    "documents",
    "team",
  ],
  third_party: ["equipment", "walk-scheduler"],
};

export const hasProjectTabAccess = ({ systemRole, projectRole, tabId }) => {
  if (isSystemAdmin(systemRole)) return true;
  if (!projectRole) return tabId === "dashboard";
  // New isolated PDF Splitter is available to every existing project role.
  if (tabId === "pdf-splitter") return PROJECT_ROLE_VALUES.includes(normProjectRole(projectRole));

  const allowed = ROLE_TABS[normProjectRole(projectRole)] || ["dashboard"];
  return allowed.includes(tabId);
};
